/**
 * ============================================================
 * KEJJA OS — src/lib/adminApi.ts (v4 — analytics + system + admins)
 * ============================================================
 */

const API_BASE = '/api/v1';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data as T;
}

export interface SystemStats {
  users: { total: number; active: number; activeWeek: number; new7d: number; new30d: number };
  homes: { total: number; active: number; new30d: number };
  communities: { total: number; active: number };
  activity: { finance_transactions: number; pantry_items: number; community_posts: number; notifications_24h: number };
  byCountry: Array<{ country: string; homes: number }>;
}
export interface AdminUser {
  id: string; email: string; displayName: string; phoneNumber: string | null;
  emailVerified: boolean; isActive: boolean; isSuperAdmin: boolean;
  lastLoginAt: string | null; onboardedAt: string | null;
  createdAt: string; deletedAt: string | null; homeCount: number;
}
export interface AdminHome {
  id: string; name: string; motto: string | null; country: string | null; currencyCode: string;
  memberCount: number; ownerEmail: string | null; ownerName: string | null;
  createdAt: string; deletedAt: string | null;
}
export interface GrowthPoint { date: string; signups: number; homes_created: number }

export interface UserDetail extends AdminUser {
  preferredLanguage: string; timezone: string;
  homes: Array<{ homeId: string; homeName: string; role: string; joinedAt: string }>;
  ownedHomeCount: number; memberHomeCount: number;
  activity: { notifications30d: number; pushSubscriptions: number; adminSessions: number };
}

export interface HomeDetail extends AdminHome {
  region: string | null; timezone: string;
  members: Array<{ userId: string; email: string; displayName: string; role: string; joinedAt: string; isActive: boolean }>;
  activity: { financeTransactions: number; pantryItems: number; routinesActive: number; calendarEvents: number; communitiesJoined: number };
}

export interface FinanceSnapshot {
  totalTPV30d: number; totalTPV7d: number;
  avgHomeMonthlyTxCount: number; avgHomeBudget: number;
  topCategories: Array<{ categoryId: string; label: string; totalAmount: number; txCount: number }>;
  topMerchants:  Array<{ name: string; totalAmount: number; txCount: number }>;
  currencyMix:   Array<{ currency: string; homes: number }>;
}

export interface ContentSnapshot {
  topRecipes: Array<{ recipeId: string; name: string; timesUsed: number; emoji: string | null }>;
  pantryTopItems: Array<{ name: string; emoji: string | null; homesUsing: number }>;
  communityTopByPosts: Array<{ id: string; name: string; type: string; postCount: number }>;
}

export interface EngagementSnapshot {
  dau: number; wau: number; mau: number;
  topStreakHomes: Array<{ homeId: string; homeName: string; streakDays: number; routineName: string | null }>;
  recentActivity: Array<{ kind: string; id: string; label: string; subtitle: string | null; at: string }>;
}

export interface SystemInfo {
  nodeVersion: string; uptimeSeconds: number; processMemoryMB: number;
  startedAt: string;
  schedulerTasks: Array<{ name: string; lastRun: string | null }>;
  dbStats: Array<{ table: string; rows: number; bytes: string }>;
}

export interface AdminUserRow {
  id: string; email: string; displayName: string; isSuperAdmin: boolean;
  lastLoginAt: string | null; grantedAt: string | null;
}

export const adminApi = {
  // Auth
  login:       (email: string, password: string) => apiFetch<{ ok: true; otpSessionId: string; maskedEmail: string }>(`/admin-auth/login`, { method: 'POST', body: JSON.stringify({ email, password }) }),
  verifyOtp:   (otpSessionId: string, code: string, rememberMe?: boolean) => apiFetch<{ ok: true; user: { id: string; email: string; displayName: string } }>(`/admin-auth/verify-otp`, { method: 'POST', body: JSON.stringify({ otpSessionId, code, rememberMe: !!rememberMe }) }),
  me:          () => apiFetch<{ user: { id: string; email: string; displayName: string } }>(`/admin-auth/me`),
  logout:      () => apiFetch<{ ok: true }>(`/admin-auth/logout`, { method: 'POST' }),

  // Core data
  stats:       () => apiFetch<SystemStats>(`/admin/stats`),
  growth:      (days = 30) => apiFetch<{ points: GrowthPoint[] }>(`/admin/growth?days=${days}`),
  users:       (opts: { limit?: number; offset?: number; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit !== undefined)  q.set('limit',  String(opts.limit));
    if (opts.offset !== undefined) q.set('offset', String(opts.offset));
    if (opts.search) q.set('search', opts.search);
    return apiFetch<{ users: AdminUser[]; total: number }>(`/admin/users?${q.toString()}`);
  },
  userDetail:  (userId: string) => apiFetch<UserDetail>(`/admin/users/${userId}`),
  homes:       (opts: { limit?: number; offset?: number; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit !== undefined)  q.set('limit',  String(opts.limit));
    if (opts.offset !== undefined) q.set('offset', String(opts.offset));
    if (opts.search) q.set('search', opts.search);
    return apiFetch<{ homes: AdminHome[]; total: number }>(`/admin/homes?${q.toString()}`);
  },
  homeDetail:  (homeId: string) => apiFetch<HomeDetail>(`/admin/homes/${homeId}`),
  setSuperAdmin:  (userId: string, value: boolean) => apiFetch(`/admin/users/${userId}/super-admin`, { method: 'POST', body: JSON.stringify({ value }) }),
  deactivateUser: (userId: string) => apiFetch(`/admin/users/${userId}/deactivate`, { method: 'POST', body: '{}' }),
  reactivateUser: (userId: string) => apiFetch(`/admin/users/${userId}/reactivate`, { method: 'POST', body: '{}' }),
  revokeSessions: (userId: string) => apiFetch<{ revoked: number }>(`/admin/users/${userId}/revoke-sessions`, { method: 'POST', body: '{}' }),

  // Analytics
  financeSnapshot:    () => apiFetch<FinanceSnapshot>(`/admin/analytics/finance`),
  contentSnapshot:    () => apiFetch<ContentSnapshot>(`/admin/analytics/content`),
  engagementSnapshot: () => apiFetch<EngagementSnapshot>(`/admin/analytics/engagement`),

  // System
  systemInfo:  () => apiFetch<SystemInfo>(`/admin/system`),

  // Admins
  listAdmins:  () => apiFetch<{ admins: AdminUserRow[] }>(`/admin/admins`),
  grantAdmin:  (email: string) => apiFetch<{ ok: true; user: AdminUserRow }>(`/admin/admins/grant`, { method: 'POST', body: JSON.stringify({ email }) }),
};
